import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { CoinsIcon as Coins, ArrowsClockwiseIcon as ArrowsClockwise, TrophyIcon as Trophy } from "@phosphor-icons/react";
import { GoldDivider, Icon } from "@/components/luxury";

/**
 * Admin → Buyer Pass kennel referrals.
 *
 * Headline metrics card → top-10 leaderboard → recent payouts table.
 * The "Run now" button triggers the same daily cron that the
 * background loop runs at 00:00 UTC every day.
 */
export default function AdminReferrals() {
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/referrals/summary"),
        api.get(filter ? `/admin/referrals/payouts?status=${filter}` : "/admin/referrals/payouts"),
      ]);
      setSummary(s.data);
      setPayouts(p.data?.payouts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/automations/referral-payouts/run-now");
      toast.success(`Settled: ${r.data.settled}, pending: ${r.data.pending}, failed: ${r.data.failed}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Admin / Referrals</p>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Buyer Pass referrals
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl leading-relaxed">
              When a buyer subscribes to Buyer Pass after first landing on a kennel
              page, that kennel earns 20% of their first 12 months' subscription
              revenue, paid via Stripe Connect.
            </p>
          </div>
          <Button onClick={runNow} disabled={busy} className="rounded-full" data-testid="referrals-run-now">
            <ArrowsClockwise size={14} className="mr-1.5" />
            {busy ? "Running…" : "Run payouts now"}
          </Button>
        </div>
        <GoldDivider className="mt-8 mb-10 max-w-md" />

        {summary && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat label="Total Buyer Pass subs" value={summary.buyer_pass_subscriptions_total} />
            <Stat label="Attributed to kennels" value={`${summary.attributed_to_kennel_count} (${summary.attribution_rate_pct}%)`} />
            <Stat label="Paid out" value={`$${summary.total_paid_usd}`} tone="gold" />
            <Stat label="Pending payout" value={`$${summary.total_pending_usd}`} tone={summary.total_pending_usd > 0 ? "crimson" : "muted"} />
          </div>
        )}

        {/* Leaderboard */}
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-8 shadow-luxury-light">
          <div className="flex items-center gap-2 mb-1">
            <Icon as={Trophy} tone="gold" size="sm" weight="duotone" />
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Top 10 referring kennels</p>
          </div>
          <h2 className="font-spectral text-2xl tracking-tight">Where the buyers came from</h2>
          <div className="gold-hairline mt-4 mb-4" />
          {(summary?.leaderboard || []).length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">No paid referrals yet — buyers haven't completed their first Buyer Pass via a kennel page.</p>
          ) : (
            <ul className="divide-y divide-[#EAEAEA]" data-testid="referrals-leaderboard">
              {summary.leaderboard.map((row, i) => (
                <li key={row.kennel_id} className="flex items-center justify-between gap-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-spectral text-2xl text-[#6B6B6B] w-8">{i + 1}</span>
                    <Link to={`/breeders/${row.kennel_id}`} className="font-medium text-ink hover:text-crimson truncate">
                      {row.kennel_name}
                    </Link>
                  </div>
                  <div className="text-right">
                    <div className="font-spectral text-lg text-[#8A6A00]">${(row.share_cents / 100).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B]">{row.invoices} invoice{row.invoices === 1 ? "" : "s"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payouts table */}
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 shadow-luxury-light">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Ledger</p>
              <h2 className="font-spectral text-2xl tracking-tight">Recent payouts</h2>
            </div>
            <select
              className="text-sm border border-[#E5E5E5] rounded-md p-1.5"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="referrals-filter"
            >
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="pending_onboarding">Pending onboarding</option>
              <option value="error">Errors</option>
              <option value="rejected_kennel_missing">Rejected (kennel missing)</option>
            </select>
          </div>
          {payouts.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">No payouts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="referrals-payouts-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] border-b border-[#EAEAEA]">
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Kennel</th>
                    <th className="py-2 pr-2">Subscription</th>
                    <th className="py-2 pr-2 text-right">Gross</th>
                    <th className="py-2 pr-2 text-right">Share</th>
                    <th className="py-2 pr-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-b border-[#F4F4F4] last:border-0">
                      <td className="py-2 pr-2"><StatusPill status={p.status} /></td>
                      <td className="py-2 pr-2 font-mono text-[11px] text-[#3a3a3a]">{p.kennel_id?.slice(0, 8)}…</td>
                      <td className="py-2 pr-2 font-mono text-[11px] text-[#6B6B6B]">{p.subscription_id?.slice(0, 8)}…</td>
                      <td className="py-2 pr-2 text-right font-tabular">${(p.gross_cents / 100).toFixed(2)}</td>
                      <td className="py-2 pr-2 text-right font-tabular text-[#8A6A00]">${(p.share_cents / 100).toFixed(2)}</td>
                      <td className="py-2 pr-2 text-[11px] text-[#6B6B6B]">{(p.paid_at || p.attempted_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] text-[#6B6B6B] italic text-center mt-10 flex items-center justify-center gap-2">
          <Icon as={Coins} tone="muted" size="xs" weight="duotone" />
          20% of the first 12 months. Paid only when the kennel has Stripe Connect onboarded; otherwise accrues until they do.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tone = "default" }) {
  const cls = {
    default: "bg-white border-[#EAEAEA]",
    gold: "bg-cream border-[#E2DAC2]",
    crimson: "bg-[#FBEBEC] border-[#E5BEC0]",
    muted: "bg-[#FAFAFA] border-[#EAEAEA]",
  }[tone];
  return (
    <div className={`${cls} border rounded-xl p-5`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">{label}</p>
      <p className="font-spectral text-3xl tracking-tight">{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = {
    paid:                       { label: "Paid",      bg: "bg-[#FBF7EC]", fg: "text-[#8A6A00]", border: "border-[#E5D08A]" },
    pending_onboarding:         { label: "Pending",   bg: "bg-[#FBEBEC]", fg: "text-[#A4222A]", border: "border-[#E5BEC0]" },
    error:                      { label: "Error",     bg: "bg-[#FBEBEC]", fg: "text-[#A4222A]", border: "border-[#E5BEC0]" },
    rejected_kennel_missing:    { label: "Rejected",  bg: "bg-[#F4F4F4]", fg: "text-[#6B6B6B]", border: "border-[#E5E5E5]" },
  }[status] || { label: status || "—", bg: "bg-[#F4F4F4]", fg: "text-[#6B6B6B]", border: "border-[#E5E5E5]" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] border ${meta.bg} ${meta.fg} ${meta.border}`}>
      {meta.label}
    </span>
  );
}
